import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize, MoveHorizontal } from "lucide-react";
import { useLang } from "@/i18n";

const PANO =
  "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=2000&q=85";

function VrIcon({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className} aria-hidden="true">
      <path d="M3 8.5A2.5 2.5 0 0 1 5.5 6h13A2.5 2.5 0 0 1 21 8.5v5a2.5 2.5 0 0 1-2.5 2.5h-3.1a2 2 0 0 1-1.5-.7L12 13l-1.9 2.3a2 2 0 0 1-1.5.7H5.5A2.5 2.5 0 0 1 3 13.5v-5z" strokeLinejoin="round" />
      <circle cx="8.2" cy="11" r="1" fill="currentColor" stroke="none" />
      <circle cx="15.8" cy="11" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

export default function PanoPlayer({ playSignal = 0 }) {
  const { t } = useLang();
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [touched, setTouched] = useState(false);
  const [timeSec, setTimeSec] = useState(14);
  const wrapRef = useRef(null);
  const stripRef = useRef(null);
  const progBarRef = useRef(null);
  const panRef = useRef(12);
  const dragRef = useRef(null);

  const setPan = (v) => {
    const pan = ((v % 50) + 50) % 50;
    panRef.current = pan;
    if (stripRef.current) stripRef.current.style.transform = `translate3d(${-pan}%, 0, 0)`;
    if (progBarRef.current) progBarRef.current.style.width = `${(pan / 50) * 100}%`;
  };

  useEffect(() => {
    if (playSignal > 0) {
      setPlaying(true);
      setTouched(true);
    }
  }, [playSignal]);

  useEffect(() => {
    if (!playing) return undefined;
    let raf;
    let last = performance.now();
    const tick = (now) => {
      const dt = now - last;
      last = now;
      setPan(panRef.current + dt * 0.0022);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const timer = setInterval(() => {
      setTimeSec(Math.floor((panRef.current / 50) * 100 * 0.6));
    }, 500);
    return () => {
      cancelAnimationFrame(raf);
      clearInterval(timer);
    };
  }, [playing]);

  const onPointerDown = (e) => {
    dragRef.current = { x: e.clientX, pan: panRef.current, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d || !wrapRef.current) return;
    const w = wrapRef.current.clientWidth || 1;
    const dx = e.clientX - d.x;
    if (Math.abs(dx) > 4) {
      d.moved = true;
      setTouched(true);
    }
    setPan(d.pan + (dx / w) * 42);
  };

  const onPointerUp = () => {
    if (dragRef.current && !dragRef.current.moved) {
      setPlaying((p) => !p);
      setTouched(true);
    }
    dragRef.current = null;
  };

  const toggleFullscreen = () => {
    const el = wrapRef.current;
    if (!el) return;
    if (!document.fullscreenElement) el.requestFullscreen?.();
    else document.exitFullscreen();
  };

  return (
    <div
      ref={wrapRef}
      data-testid="interactive-360-player-container"
      className="relative w-full aspect-[4/3] sm:aspect-[16/10] overflow-hidden rounded-3xl border border-cyan-400/25 bg-slate-900 shadow-[0_0_60px_rgba(0,240,255,0.12),0_30px_80px_rgba(0,0,0,0.6)] select-none"
    >
      <div
        data-testid="panoramic-view-canvas"
        className="absolute inset-0 cursor-grab active:cursor-grabbing touch-none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
      >
        <div ref={stripRef} className="pano-strip flex h-full w-[200%]" style={{ transform: "translate3d(-12%, 0, 0)" }}>
          <div className="h-full w-1/2 bg-cover bg-center" style={{ backgroundImage: `url(${PANO})` }} />
          <div className="h-full w-1/2 bg-cover bg-center" style={{ backgroundImage: `url(${PANO})` }} />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#030712]/90 via-transparent to-[#030712]/40 pointer-events-none" />
      </div>

      <div className="absolute top-4 left-4 flex items-center gap-2 rounded-full border border-white/15 bg-[#030712]/60 backdrop-blur-md px-4 min-h-10 pointer-events-none">
        <VrIcon className="w-4 h-4 text-cyan-300" />
        <span className="font-mono-accent text-[11px] uppercase tracking-[0.18em] text-cyan-200">
          {t.hero.playerBadge}
        </span>
      </div>

      <div className="absolute top-4 right-4 flex items-center gap-2 rounded-full bg-[#030712]/60 backdrop-blur-md border border-white/15 px-4 min-h-10 pointer-events-none">
        <span className={`h-2 w-2 rounded-full ${playing ? "bg-cyan-400 animate-pulse" : "bg-slate-500"}`} />
        <span className="font-mono-accent text-[11px] uppercase tracking-[0.18em] text-slate-300">
          {t.hero.live}
        </span>
      </div>

      <svg
        viewBox="0 0 100 100"
        className="pointer-events-none absolute inset-0 m-auto w-52 h-52 sm:w-64 sm:h-64 opacity-70"
        aria-hidden="true"
      >
        <g className="animate-spin-slower" style={{ transformOrigin: "50% 50%" }}>
          <circle cx="50" cy="50" r="47" fill="none" stroke="rgba(0,240,255,0.55)" strokeWidth="0.6" strokeDasharray="2.5 3.5" />
          <path d="M50 1.5l1.8 3.4h-3.6z" fill="#00f0ff" />
        </g>
        <g className="animate-spin-rev" style={{ transformOrigin: "50% 50%" }}>
          <circle cx="50" cy="50" r="38" fill="none" stroke="rgba(139,92,246,0.5)" strokeWidth="0.5" strokeDasharray="1 4" />
          <path d="M50 89.5l1.6 3h-3.2z" fill="#8b5cf6" />
        </g>
      </svg>

      {!playing && (
        <button
          data-testid="player-play-overlay"
          onClick={() => {
            setPlaying(true);
            setTouched(true);
          }}
          aria-label="Play 360 tour"
          className="absolute inset-0 m-auto flex h-20 w-20 items-center justify-center rounded-full border border-cyan-300/50 bg-[#030712]/55 backdrop-blur-md text-cyan-200 hover:scale-110 hover:shadow-[0_0_40px_rgba(0,240,255,0.4)] transition-all duration-300"
        >
          <span className="absolute inset-0 rounded-full border border-cyan-400/30 animate-ping" />
          <Play className="w-8 h-8 ml-1 fill-current" />
        </button>
      )}

      {!touched && (
        <div className="absolute bottom-24 inset-x-0 flex justify-center pointer-events-none">
          <div className="flex items-center gap-2 rounded-full bg-[#030712]/70 backdrop-blur-md border border-white/15 px-5 min-h-10">
            <MoveHorizontal className="w-4 h-4 text-cyan-300" />
            <span className="text-xs text-slate-300">{t.hero.dragHint}</span>
          </div>
        </div>
      )}

      <div
        data-testid="player-control-bar"
        className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#030712] via-[#030712]/80 to-transparent px-4 sm:px-5 pt-8 pb-3"
      >
        <div className="mb-3 h-1 w-full rounded-full bg-white/15 overflow-hidden">
          <div
            ref={progBarRef}
            className="h-full rounded-full bg-gradient-to-r from-cyan-400 via-blue-500 to-violet-500"
            style={{ width: "24%" }}
          />
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            data-testid="player-play-toggle"
            onClick={() => {
              setPlaying((p) => !p);
              setTouched(true);
            }}
            aria-label="Play or pause"
            className="flex h-11 w-11 items-center justify-center rounded-full text-slate-200 hover:text-cyan-300 hover:bg-white/10 transition-colors duration-200"
          >
            {playing ? <Pause className="w-5 h-5 fill-current" /> : <Play className="w-5 h-5 ml-0.5 fill-current" />}
          </button>
          <button
            data-testid="player-volume-toggle"
            onClick={() => setMuted((m) => !m)}
            aria-label="Toggle volume"
            className="flex h-11 w-11 items-center justify-center rounded-full text-slate-200 hover:text-cyan-300 hover:bg-white/10 transition-colors duration-200"
          >
            {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
          <span className="font-mono-accent text-[11px] tracking-widest text-slate-400 ml-1">00:{String(timeSec).padStart(2, "0")} / 01:00</span>
          <span className="ml-auto font-mono-accent text-[11px] tracking-widest text-cyan-400/80 hidden sm:block">4K • 360°</span>
          <button
            data-testid="player-fullscreen-toggle"
            onClick={toggleFullscreen}
            aria-label="Toggle fullscreen"
            className="flex h-11 w-11 items-center justify-center rounded-full text-slate-200 hover:text-cyan-300 hover:bg-white/10 transition-colors duration-200"
          >
            <Maximize className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
